import { FunctionComponent, useCallback, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Dropzone } from '@starjack/shared-components';
import { useNotification } from '@starjack/features/notifications';
import { useVoucherTemplateFiles } from '../hooks/useVoucherTemplateFiles';

const ACCEPTED_PDF = { 'application/pdf': ['.pdf'] };

type BackgroundUploadDialogProps = {
    open: boolean;
    onClose: () => void;
    templateId: string | null;
    onSuccess: () => void;
};

export const BackgroundUploadDialog: FunctionComponent<
    BackgroundUploadDialogProps
> = ({ open, onClose, templateId, onSuccess }) => {
    const { uploadBackground, isUploading } = useVoucherTemplateFiles();
    const { addNotification } = useNotification();
    const [file, setFile] = useState<File | null>(null);

    const handleDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length > 0) {
            setFile(acceptedFiles[0]);
        }
    }, []);

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

    const handleUpload = useCallback(async () => {
        if (!file || !templateId) return;
        if (file.size > MAX_FILE_SIZE) {
            addNotification({
                type: 'error',
                message: `Datei zu groß (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum: 10 MB.`,
            });
            return;
        }
        const success = await uploadBackground(templateId, file);
        if (success) {
            setFile(null);
            onSuccess();
            onClose();
        }
    }, [file, templateId, uploadBackground, onSuccess, onClose]);

    const handleClose = useCallback(() => {
        setFile(null);
        onClose();
    }, [onClose]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>Hintergrund-PDF hochladen</DialogTitle>
            <DialogContent>
                <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ mb: 2 }}
                >
                    Laden Sie eine PDF-Datei als Blanko-Hintergrund für die
                    Gutscheinvorlage hoch. Empfohlenes Format: A4 (210 x 297
                    mm). Maximale Dateigröße: 10 MB.
                </Typography>

                <Dropzone
                    onDrop={handleDrop}
                    accept={ACCEPTED_PDF}
                    maxFiles={1}
                />

                {file && (
                    <Typography variant='body2' sx={{ mt: 1 }}>
                        Ausgewählt: {file.name} (
                        {(file.size / 1024 / 1024).toFixed(2)} MB)
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Abbrechen</Button>
                <LoadingButton
                    onClick={handleUpload}
                    loading={isUploading}
                    disabled={!file}
                    variant='contained'
                >
                    Hochladen
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};
